import { useState, useMemo } from 'react';
import { CreditCard, Plus, X, Check, AlertCircle, User, Search } from 'lucide-react';
import { colors, Card, Button, LoadingSpinner } from '../shared';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const STATUS_STYLES = {
  active: { bg: colors.greenDim, color: colors.green, label: 'Active' },
  locked: { bg: colors.orangeDim, color: colors.orange, label: 'Locked' },
  closed: { bg: colors.redDim, color: colors.red, label: 'Closed' },
};

export default function CardAssignment() {
  const { userProfile, canWrite } = useAuth();
  const {
    getCardsForUser,
    getAvailableCards,
    assignCardToSelf,
    unassignCardFromSelf,
    cards,
  } = useData();

  const isDemoUser = userProfile?.role === 'demo';

  const [assigning, setAssigning] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  // Get user's assigned cards
  const myCards = useMemo(() => getCardsForUser(), [getCardsForUser]);

  // Determine if cards are from explicit assignment or inference
  const hasExplicitAssignment = (userProfile?.assignedCards?.length || 0) > 0;

  // Get available cards for self-assignment
  const availableCards = useMemo(() => {
    const available = getAvailableCards();
    if (!searchTerm) return available;

    const term = searchTerm.toLowerCase();
    return available.filter(c =>
      c.cardLast4.includes(term) ||
      (c.holderName || '').toLowerCase().includes(term) ||
      (c.cardName || '').toLowerCase().includes(term)
    );
  }, [getAvailableCards, searchTerm]);

  const handleAssign = async (cardLast4) => {
    setAssigning(true);
    setError(null);
    try {
      await assignCardToSelf(cardLast4);
      setShowPicker(false);
      setSearchTerm('');
    } catch (err) {
      console.error('Error assigning card:', err);
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (cardLast4) => {
    if (!confirm('Remove this card from your account? You can add it back later.')) {
      return;
    }

    setAssigning(true);
    setError(null);
    try {
      await unassignCardFromSelf(cardLast4);
    } catch (err) {
      console.error('Error unassigning card:', err);
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* My Cards Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium" style={{ color: colors.textPrimary }}>
            My Cards
          </h3>
          <p className="text-xs" style={{ color: colors.textMuted }}>
            {isDemoUser
              ? 'Demo mode - read only'
              : hasExplicitAssignment
                ? 'Explicitly assigned to you'
                : myCards.length > 0
                  ? 'Matched by name (claim to confirm)'
                  : 'No cards assigned'}
          </p>
        </div>
        {!isDemoUser && (
          <Button
            variant="secondary"
            onClick={() => setShowPicker(!showPicker)}
            className="text-sm"
          >
            <Plus size={16} />
            Claim Card
          </Button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div
          className="p-3 rounded-xl flex items-center gap-2 text-sm"
          style={{ background: colors.redDim, color: colors.red }}
        >
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Card Picker Modal */}
      {showPicker && (
        <Card className="border" style={{ borderColor: colors.yellow }}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium" style={{ color: colors.textPrimary }}>
              Claim a Card
            </h4>
            <button onClick={() => setShowPicker(false)} style={{ color: colors.textMuted }}>
              <X size={18} />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: colors.textMuted }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by last 4 or name..."
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm outline-none"
              style={{
                background: colors.inputBg,
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
              }}
            />
          </div>

          {/* Available Cards List */}
          <div className="max-h-60 overflow-auto space-y-2">
            {availableCards.length === 0 ? (
              <p className="text-sm text-center py-4" style={{ color: colors.textMuted }}>
                {searchTerm ? 'No matching cards found' : 'No available cards'}
              </p>
            ) : (
              availableCards.map(card => (
                <div
                  key={card.cardLast4}
                  className="flex items-center justify-between p-2 rounded-lg"
                  style={{ background: colors.inputBg }}
                >
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} style={{ color: colors.textMuted }} />
                    <div>
                      <p className="font-mono text-sm font-medium" style={{ color: colors.textPrimary }}>
                        ****{card.cardLast4}
                      </p>
                      <p className="text-xs" style={{ color: colors.textSecondary }}>
                        {card.holderName || card.cardName || 'Unassigned'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAssign(card.cardLast4)}
                    disabled={assigning}
                    className="p-1.5 rounded-lg transition-all"
                    style={{ background: colors.greenDim, color: colors.green }}
                  >
                    {assigning ? <LoadingSpinner size="sm" color={colors.green} /> : <Plus size={16} />}
                  </button>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {/* My Cards List */}
      {myCards.length === 0 ? (
        <Card className="text-center py-8">
          <CreditCard size={32} className="mx-auto mb-2" style={{ color: colors.textMuted }} />
          <p style={{ color: colors.textSecondary }}>No cards assigned to you</p>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
            Click "Claim Card" to add your company card
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {myCards.map(card => {
            const statusStyle = STATUS_STYLES[card.status] || STATUS_STYLES.active;
            const isExplicit = (userProfile?.assignedCards || []).includes(card.cardLast4);

            return (
              <Card key={card.cardLast4} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ background: colors.inputBg }}
                  >
                    <CreditCard size={20} style={{ color: colors.yellow }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-medium" style={{ color: colors.textPrimary }}>
                        ****{card.cardLast4}
                      </p>
                      <span
                        className="px-1.5 py-0.5 rounded text-xs"
                        style={{ background: statusStyle.bg, color: statusStyle.color }}
                      >
                        {statusStyle.label}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>
                      {card.cardName || card.holderName || 'Unknown'}
                    </p>
                    {!isExplicit && (
                      <p className="text-xs" style={{ color: colors.textMuted }}>
                        Matched by name - click to confirm
                      </p>
                    )}
                  </div>
                </div>

                {!isDemoUser && (
                  <div className="flex items-center gap-2">
                    {!isExplicit && (
                      <button
                        onClick={() => handleAssign(card.cardLast4)}
                        disabled={assigning}
                        className="p-2 rounded-lg transition-all"
                        style={{ background: colors.greenDim, color: colors.green }}
                        title="Confirm this card"
                      >
                        <Check size={16} />
                      </button>
                    )}
                    {isExplicit && (
                      <button
                        onClick={() => handleUnassign(card.cardLast4)}
                        disabled={assigning}
                        className="p-2 rounded-lg transition-all"
                        style={{ background: colors.redDim, color: colors.red }}
                        title="Remove card"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Info about card matching */}
      {!hasExplicitAssignment && myCards.length > 0 && (
        <Card className="text-sm" style={{ background: colors.yellowDim }}>
          <div className="flex items-start gap-2">
            <AlertCircle size={16} style={{ color: colors.yellow }} className="flex-shrink-0 mt-0.5" />
            <div style={{ color: colors.yellow }}>
              <p className="font-medium">Cards matched by name</p>
              <p className="opacity-80">
                These cards were matched based on your display name. Click the checkmark to confirm
                they're yours, or claim different cards if needed.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
